import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from 'urql';
import {
  GetSocialAnalyticsSummaryWithPreviousPeriodQuery,
  GetSocialAnalyticsSummaryWithPreviousPeriodQueryVariables,
} from '@frontend/gql/social/graphql';
import { getSocialAnalyticsSummaryWithPreviousPeriod } from '../graphql';

type RequiredKeys = Exclude<
  keyof GetSocialAnalyticsSummaryWithPreviousPeriodQueryVariables,
  'projectIds' | 'groupIds' | 'segments' | 'memberIds' | 'previousStartDate' | 'previousEndDate'
>;
type GraphQLQueryVariables = Required<Pick<GetSocialAnalyticsSummaryWithPreviousPeriodQueryVariables, RequiredKeys>> &
  Partial<
    Pick<
      GetSocialAnalyticsSummaryWithPreviousPeriodQueryVariables,
      'projectIds' | 'groupIds' | 'segments' | 'memberIds'
    >
  >;

interface UseSummaryDataProps {
  clientId: string | undefined;
  tab: string;
  queryVariables: GraphQLQueryVariables;
}

interface UseSummaryDataReturn {
  data: GetSocialAnalyticsSummaryWithPreviousPeriodQuery | undefined;
  fetching: boolean;
  refetching: boolean;
  handleRefresh: () => void;
}

// Helper function to validate date string
const isValidDateString = (dateString: string): boolean => {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }

  const date = new Date(dateString);
  return !isNaN(date.getTime()) && /^\d{4}-\d{2}-\d{2}$/.test(dateString);
};

// Helper function to calculate previous period dates
const getPreviousIntervalRange = (currentRange: { startDate: string; endDate: string }) => {
  // Validate input dates
  if (!isValidDateString(currentRange.startDate)) {
    throw new Error(`Invalid startDate format: ${currentRange.startDate}. Expected YYYY-MM-DD format.`);
  }

  if (!isValidDateString(currentRange.endDate)) {
    throw new Error(`Invalid endDate format: ${currentRange.endDate}. Expected YYYY-MM-DD format.`);
  }

  const startDate = new Date(currentRange.startDate);
  const endDate = new Date(currentRange.endDate);

  // Additional validation: ensure end date is not before start date
  if (endDate.getTime() < startDate.getTime()) {
    throw new Error(`End date (${currentRange.endDate}) cannot be before start date (${currentRange.startDate}).`);
  }

  const currentTimeSpan = endDate.getTime() - startDate.getTime();
  // Add 1 day (in milliseconds) to ensure we always go back at least one full period
  // This fixes the issue where single-day selections would have timespan of 0
  const periodLength = currentTimeSpan + 24 * 60 * 60 * 1000;

  return {
    startDate: new Date(startDate.getTime() - periodLength).toISOString().split('T')[0],
    endDate: new Date(endDate.getTime() - periodLength).toISOString().split('T')[0],
  };
};

export const useSummaryData = ({ clientId, tab, queryVariables }: UseSummaryDataProps): UseSummaryDataReturn => {
  const [refetching, setRefetching] = useState(false);

  // Calculate previous period dates
  const previousPeriod = useMemo(() => {
    // Helper function to safely convert date to string
    const getDateString = (date: string | Date | undefined): string => {
      if (!date) {
        // Fallback to today's date if undefined (for testing scenarios)
        return new Date().toISOString().split('T')[0];
      }

      if (typeof date === 'string') {
        return date;
      }

      // If it's a Date object, convert to ISO string
      return date.toISOString().split('T')[0];
    };

    const startDateString = getDateString(queryVariables.startDate);
    const endDateString = getDateString(queryVariables.endDate);

    return getPreviousIntervalRange({
      startDate: startDateString,
      endDate: endDateString,
    });
  }, [queryVariables.startDate, queryVariables.endDate]);

  const [{ data, fetching }, refetch] = useQuery({
    query: getSocialAnalyticsSummaryWithPreviousPeriod,
    variables: {
      ...queryVariables,
      previousStartDate: previousPeriod.startDate,
      previousEndDate: previousPeriod.endDate,
      segments: queryVariables.segments || [],
      memberIds: queryVariables.memberIds || [],
    },
    pause: !clientId || tab !== 'summary',
  });

  const handleRefresh = useCallback(() => {
    if (tab === 'summary') {
      setRefetching(true);
      refetch({ requestPolicy: 'network-only' });
    }
  }, [tab, refetch]);

  useEffect(() => {
    if (!fetching) {
      setRefetching(false);
    }
  }, [fetching]);

  return {
    data,
    fetching,
    refetching,
    handleRefresh,
  };
};
